/**
 * Compact "facts" table rendered in the middle of the diagnostics dialog.
 *
 * Mirrors `Ralph/ConnectionDiagnostics/PRD.md` → Slice 3 → "Middle — facts".
 * No editorialising — just the raw signals from the diagnostics battery.
 */

import type { DiagnosticsReport } from '../../../types/connectionDiagnostics';

function fmtBool(value: boolean | undefined): string {
  if (value === undefined) return 'unknown';
  return value ? 'yes' : 'no';
}

function fmtListingError(report: DiagnosticsReport): string {
  const err = report.databases.listingError;
  if (!err) return '—';
  if (err.sqlErrorCode !== undefined) return `${err.sqlErrorCode}`;
  return err.message ? '(error)' : '—';
}

function fmtEngineEdition(report: DiagnosticsReport): string {
  const edition = report.serverEdition;
  if (!edition) return 'unknown';
  const slug = edition.engineEdition;
  const label =
    slug === 'azure_sql_db'
      ? 'Azure SQL Database'
      : slug === 'azure_sql_managed_instance'
        ? 'Azure SQL Managed Instance'
        : slug === 'azure_synapse'
          ? 'Azure Synapse'
          : slug === 'azure_synapse_serverless'
            ? 'Azure Synapse (serverless)'
            : slug === 'azure_sql_edge'
              ? 'Azure SQL Edge'
              : slug === 'sqlserver_enterprise'
                ? 'SQL Server (Enterprise)'
                : slug === 'sqlserver_standard'
                  ? 'SQL Server (Standard)'
                  : slug === 'sqlserver_express'
                    ? 'SQL Server (Express)'
                    : slug === 'sqlserver_personal'
                      ? 'SQL Server (Personal/Developer)'
                      : edition.edition || 'unknown';
  return label;
}

function fmtListingDuration(report: DiagnosticsReport): string {
  if (report.databases.listingTimedOut) return 'timed out';
  if (typeof report.databases.listingDurationMs === 'number') {
    return `${report.databases.listingDurationMs} ms`;
  }
  return '—';
}

type FactsTableProps = {
  report: DiagnosticsReport;
};

const ROW_BASE = 'border-b border-border/40 last:border-b-0';
const KEY_CELL = 'py-1.5 pr-3 text-muted-foreground align-top whitespace-nowrap';
const VALUE_CELL = 'py-1.5 align-top break-words';

export function DiagnosticsFactsTable({ report }: FactsTableProps) {
  const rows: Array<[string, string]> = [
    ['Engine edition', fmtEngineEdition(report)],
    ['Server principal', report.serverIdentity?.principal || 'unknown'],
    ['Database user', report.serverIdentity?.dbUser || 'unknown'],
    ['sysadmin role', fmtBool(report.capabilities?.isSysadmin)],
    ['VIEW ANY DATABASE', fmtBool(report.capabilities?.canViewAnyDatabase)],
    ['Access to master DB', fmtBool(report.capabilities?.hasMasterAccess)],
    ['Databases the server returned', String(report.databases.rawCount)],
    ['Of those, accessible to you (HAS_DBACCESS)', String(report.databases.accessibleCount)],
    ['Listing query error', fmtListingError(report)],
    ['Listing query took', fmtListingDuration(report)],
  ];

  return (
    <table
      data-testid="diag-facts-table"
      className="w-full text-sm border-collapse"
    >
      <tbody>
        {rows.map(([key, value]) => (
          <tr key={key} className={ROW_BASE}>
            <td className={KEY_CELL}>{key}</td>
            <td className={VALUE_CELL}>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
